import api from './api';
import { ApiResponse } from '../types';

/**
 * Finance Reports API client. The endpoint contracts are additive — adding
 * a new metric (e.g. `revenue`) is a matter of listing it in `metrics` and
 * reading the new key from the response.
 */

export type FinanceMetric = 'expenses' | 'fines' | 'revenue' | 'fees';
export type FinanceGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface FinanceReportFilters {
  dateFrom?: string;
  dateTo?: string;
  categoryId?: string;
  subCategoryId?: string;
  submitterId?: string;
  status?: string;
  method?: string;
  metrics?: FinanceMetric[];
  granularity?: FinanceGranularity;
  limit?: number;
}

export interface FinanceKpiBucket {
  amount: number;
  count: number;
}

export interface FinanceSummary {
  window: { from: string; to: string };
  /** Net change in cash and bank over the window, from the books (receipts, payments, refunds, bank charges…). */
  cash: { net: number };
  expenses: {
    total: FinanceKpiBucket;
    byStatus: { pending: FinanceKpiBucket; approved: FinanceKpiBucket; rejected: FinanceKpiBucket };
  } | null;
  fines: {
    collected: FinanceKpiBucket;
    outstanding: FinanceKpiBucket;
  } | null;
  revenue: {
    total: FinanceKpiBucket;
  } | null;
  /** Fee receipts in the window, and what is owed on live challans now. */
  fees: {
    collected: FinanceKpiBucket;
    outstanding: FinanceKpiBucket;
  } | null;
}

export interface FinanceTimeseriesBucket {
  bucket: string;
  amount: number;
  count: number;
}

export interface FinanceTimeseries {
  window: { from: string; to: string };
  granularity: FinanceGranularity;
  series: {
    expenses: FinanceTimeseriesBucket[] | null;
    fines: FinanceTimeseriesBucket[] | null;
    revenue: FinanceTimeseriesBucket[] | null;
    fees: FinanceTimeseriesBucket[] | null;
  };
}

export interface CategoryBreakdownItem {
  categoryId: string | null;
  name: string;
  amount: number;
  count: number;
}

export interface SubCategoryBreakdownItem {
  subCategoryId: string | null;
  categoryId: string | null;
  name: string;
  parentName: string | null;
  amount: number;
  count: number;
}

export interface StatusBreakdownItem {
  status: string;
  amount: number;
  count: number;
}

export interface TopSubmitterItem {
  userId: string;
  name: string;
  email?: string;
  amount: number;
  count: number;
}

/** Ranked money-in event grouped by memo (description) — reads from the ledger. */
export interface TopRevenueSourceItem {
  name: string;
  sourceType?: 'revenue' | 'expense' | 'fine_payment' | 'adjustment' | 'fee_payment' | 'refund';
  amount: number;
  count: number;
}

// Small helper — turns FinanceReportFilters into a URLSearchParams-ready map,
// dropping undefined / null entries and joining array metrics with commas.
const buildParams = (f: FinanceReportFilters = {}): string => {
  const sp = new URLSearchParams();
  if (f.dateFrom) sp.append('dateFrom', f.dateFrom);
  if (f.dateTo) sp.append('dateTo', f.dateTo);
  if (f.categoryId) sp.append('categoryId', f.categoryId);
  if (f.subCategoryId) sp.append('subCategoryId', f.subCategoryId);
  if (f.submitterId) sp.append('submitterId', f.submitterId);
  if (f.status) sp.append('status', f.status);
  if (f.method) sp.append('method', f.method);
  if (f.metrics && f.metrics.length > 0) sp.append('metrics', f.metrics.join(','));
  if (f.granularity) sp.append('granularity', f.granularity);
  if (f.limit != null) sp.append('limit', String(f.limit));
  return sp.toString();
};

const getSummary = async (filters?: FinanceReportFilters): Promise<FinanceSummary> => {
  const res = await api.get<ApiResponse<FinanceSummary>>(`/finance-reports/summary?${buildParams(filters)}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getTimeseries = async (filters?: FinanceReportFilters): Promise<FinanceTimeseries> => {
  const res = await api.get<ApiResponse<FinanceTimeseries>>(`/finance-reports/timeseries?${buildParams(filters)}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getExpensesByCategory = async (
  filters?: FinanceReportFilters
): Promise<{ expenses: CategoryBreakdownItem[] }> => {
  const res = await api.get<ApiResponse<{ expenses: CategoryBreakdownItem[] }>>(
    `/finance-reports/expenses-by-category?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getExpensesBySubCategory = async (
  filters?: FinanceReportFilters
): Promise<{ expenses: SubCategoryBreakdownItem[] }> => {
  const res = await api.get<ApiResponse<{ expenses: SubCategoryBreakdownItem[] }>>(
    `/finance-reports/expenses-by-subcategory?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getExpensesByStatus = async (
  filters?: FinanceReportFilters
): Promise<{ items: StatusBreakdownItem[] }> => {
  const res = await api.get<ApiResponse<{ items: StatusBreakdownItem[] }>>(
    `/finance-reports/expenses-by-status?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getTopSubmitters = async (
  filters?: FinanceReportFilters
): Promise<{ items: TopSubmitterItem[] }> => {
  const res = await api.get<ApiResponse<{ items: TopSubmitterItem[] }>>(
    `/finance-reports/top-submitters?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getRevenueByCategory = async (
  filters?: FinanceReportFilters
): Promise<{ items: CategoryBreakdownItem[] }> => {
  const res = await api.get<ApiResponse<{ items: CategoryBreakdownItem[] }>>(
    `/finance-reports/revenue-by-category?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getTopRevenueSources = async (
  filters?: FinanceReportFilters
): Promise<{ items: TopRevenueSourceItem[] }> => {
  const res = await api.get<ApiResponse<{ items: TopRevenueSourceItem[] }>>(
    `/finance-reports/top-revenue-sources?${buildParams(filters)}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const financeReportsService = {
  getSummary,
  getTimeseries,
  getExpensesByCategory,
  getExpensesBySubCategory,
  getExpensesByStatus,
  getTopSubmitters,
  getRevenueByCategory,
  getTopRevenueSources,
};

export default financeReportsService;
